import { forwardRef } from 'react'
import type { LeaderboardEntry, TeamStanding, ZoneBreakdown } from '@gymboard/types'
import { ZONES, ordinal } from './SpotlightStage'

/** Podium numerals: camel, wool-silver, bronze. No gold in the system. */
export const PODIUM = ['#D9B68A', '#B4B3C2', '#C98B5A']

const PLACE = ['Class winner', 'Second place', 'Third place']

function rgba(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

function zoneMs(z: ZoneBreakdown): number[] {
  return [z.zone1Ms, z.zone2Ms, z.zone3Ms, z.zone4Ms, z.zone5Ms]
}

export function minutes(ms: number): string {
  if (ms < 60_000) return '<1 min'
  const m = Math.round(ms / 60000)
  return `${m} min`
}

/**
 * The colour a class was "spent" in: the hardest zone among 3–5 that held
 * the most time, else the zone with the most time overall.
 */
function signatureZone(z: ZoneBreakdown | undefined): number {
  if (!z) return -1
  const ms = zoneMs(z)
  if (ms.every((v) => v <= 0)) return -1
  const hard = [2, 3, 4].reduce((best, i) => (ms[i] > ms[best] ? i : best), 2)
  if (ms[hard] > 0) return hard
  return ms.reduce((best, v, i) => (v > ms[best] ? i : best), 0)
}

interface Props {
  hero: LeaderboardEntry
  total: number
  unit: string
  teams?: TeamStanding[]
  /** Changes with each curtain call so the supporting lines replay. */
  swapKey: string
}

/**
 * End-of-class stage. The same panel the room watched all class, now
 * taking a bow: third, second, then the winner, each with what they did.
 * The numeral is the final score in that place's podium colour; the zone
 * flood shows how much of the class they spent in zones 4–5.
 */
const CurtainStage = forwardRef<HTMLDivElement, Props>(function CurtainStage(
  { hero, total, unit, teams, swapKey },
  nameRef,
) {
  const z = hero.zoneBreakdown
  const ms = z ? zoneMs(z) : [0, 0, 0, 0, 0]
  const allMs = ms.reduce((a, b) => a + b, 0)
  const redMs = ms[3] + ms[4]
  const sig = signatureZone(z)
  const zc = sig >= 0 ? ZONES[sig].color : '#66678A'
  const flood = allMs > 0 ? Math.max(0.14, Math.min(1, redMs / allMs + 0.1)) : 0.14
  const place = hero.rank <= 3 ? PLACE[hero.rank - 1] : `${ordinal(hero.rank)} place`
  const numeralColor = hero.rank <= 3 ? PODIUM[hero.rank - 1] : '#F1EEE8'
  const team = hero.team != null ? teams?.find((t) => t.team === hero.team) : undefined

  const facts: string[] = []
  if (redMs >= 60_000) facts.push(`${minutes(redMs)} in zones 4–5`)
  if (hero.calories > 0) facts.push(`${Math.round(hero.calories).toLocaleString()} kcal`)
  if (hero.peakHeartRate) facts.push(`peak ${Math.round(hero.peakHeartRate)} BPM`)

  const weave = {
    backgroundImage: [
      `linear-gradient(135deg, ${zc} 25%, transparent 25%)`,
      `linear-gradient(225deg, ${zc} 25%, transparent 25%)`,
      'linear-gradient(45deg, #D9B68A 25%, transparent 25%)',
      'linear-gradient(315deg, #D9B68A 25%, transparent 25%)',
    ].join(', '),
    backgroundSize: '1.5vw 1.5vw',
    backgroundPosition: '0 0, 0 0, 0.75vw 100%, 0.75vw 100%',
  }

  return (
    <div className="sp-stage cc">
      <div
        className="sp-flood"
        style={{ background: `linear-gradient(to top, ${rgba(zc, 0.4)}, ${rgba(zc, 0)} 92%)`, transform: `scaleY(${flood})` }}
      />
      <div key={swapKey} className="sp-inner sp-swap">
        <div className="sp-top">
          <div className="sp-why">{place}</div>
          <div className="sp-standing">
            {ordinal(hero.rank)}
            <small>of {total}</small>
          </div>
        </div>
        <div className="sp-who" ref={nameRef}>{hero.athleteName}</div>
        {team && (
          <div className="sp-tags">
            <span className="sp-tag" style={{ color: team.color, background: rgba(team.color, 0.14) }}>Team {team.name}</span>
          </div>
        )}
        {facts.length > 0 && <div className="sp-story">{facts.join('  ·  ')}</div>}
        <div className="sp-hr">
          <b style={{ color: numeralColor }}>{Math.round(hero.score).toLocaleString()}</b>
          <span>{unit}</span>
        </div>
        {allMs > 0 && (
          <div className="cc-zones" aria-label="Time in each heart-rate zone">
            <div className="cc-bar">
              {ms.map((v, i) =>
                v > 0 ? <i key={i} style={{ flexGrow: v, background: ZONES[i].color }} /> : null,
              )}
            </div>
            <div className="cc-legend">
              {ms.map((v, i) => (
                <span key={i} style={{ opacity: v > 0 ? 1 : 0.35 }}>
                  <em className="sp-zc" style={{ background: ZONES[i].color }}>{i + 1}</em>
                  {v > 0 ? minutes(v) : '–'}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="sp-weave" style={weave} />
    </div>
  )
})

export default CurtainStage
